import type { TypedDocumentNode } from '@apollo/client/core';
import type {
  ManualChapter,
  ManualIndexChapter,
  ManualSearchResult,
  ManualSection,
  ManualTour,
} from '@src/src/app/aurora.types';
import gql from 'graphql-tag';

export type { ManualChapter, ManualIndexChapter, ManualSearchResult, ManualSection, ManualTour };

/**
 * The text before a chapter's first H2. The compile gives it the id `<chapter>.intro` and the
 * chapter's own title, so the reader shows it with no heading and leaves it out of the contents.
 */
export const isIntroSection = (
  chapter: Pick<ManualChapter, 'id'>,
  section: Pick<ManualSection, 'id'>,
): boolean => section.id === `${chapter.id}.intro`;

export const manualIndexQuery: TypedDocumentNode<
  { manualIndex: ManualIndexChapter[] },
  { lang: string }
> = gql`
  query ManualIndex($lang: String!) {
    manualIndex(lang: $lang) {
      id
      title
      route
      tour {
        id
        title
        screen
        steps {
          target
          title
          text
        }
      }
    }
  }
`;

export const manualSearchQuery: TypedDocumentNode<
  { manualSearch: ManualSearchResult[] },
  { lang: string; query: string; isFailureLogged?: boolean; route?: string }
> = gql`
  query ManualSearch($lang: String!, $query: String!, $isFailureLogged: Boolean, $route: String) {
    manualSearch(lang: $lang, query: $query, isFailureLogged: $isFailureLogged, route: $route) {
      id
      chapterId
      title
      route
      excerpt
    }
  }
`;

export const manualReportSearchNoClickMutation: TypedDocumentNode<
  { manualReportSearchNoClick: boolean },
  { lang: string; query: string }
> = gql`
  mutation ManualReportSearchNoClick($lang: String!, $query: String!) {
    manualReportSearchNoClick(lang: $lang, query: $query)
  }
`;

export const manualChapterQuery: TypedDocumentNode<
  { manualChapter: ManualChapter },
  { lang: string; id: string }
> = gql`
  query ManualChapter($lang: String!, $id: String!) {
    manualChapter(lang: $lang, id: $id) {
      id
      title
      sections {
        id
        title
        html
      }
    }
  }
`;
